import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function CertificateVerification() {
    const { token } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [verifying, setVerifying] = useState(false);
    const [certificate, setCertificate] = useState(null);
    const [error, setError] = useState('');
    const [valid, setValid] = useState(false);
    const [downloadMessage, setDownloadMessage] = useState('');
    const [canDownload, setCanDownload] = useState(false);

    useEffect(() => {
        verifyCertificate();
    }, [token]);

    const verifyCertificate = async () => {
        setLoading(true);
        setError('');

        try {
            const response = await axios.get(`${API_URL}/api/verify/${token}`);

            if (response.data.success && response.data.valid) {
                setCertificate(response.data.certificate);
                setValid(true);
                setCanDownload(response.data.can_download);
                setDownloadMessage(response.data.download_message);
            } else {
                setError(response.data.message || 'Certificate verification failed');
                setValid(false);
            }
        } catch (err) {
            console.error('Verification error:', err);
            if (err.response) {
                setError(err.response.data.message || 'Invalid verification code');
                setValid(false);

                // Handle specific error cases
                if (err.response.data.expired) {
                    setError('This certificate verification has expired.');
                } else if (err.response.data.inactive) {
                    setError('This certificate verification has been deactivated.');
                }
            } else {
                setError('Unable to connect to verification server');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async () => {
        if (!canDownload) {
            alert(downloadMessage);
            return;
        }

        setVerifying(true);
        try {
            const response = await axios.get(
                `${API_URL}/api/verify/${token}/download`,
                { responseType: 'blob' }
            );

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Certificate_${certificate.form_number}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            // Refresh verification info to update download count
            await verifyCertificate();

            alert('Certificate downloaded successfully!');
        } catch (err) {
            console.error('Download error:', err);
            if (err.response && err.response.status === 429) {
                alert(err.response.data.message || 'Daily download limit reached. Please try again tomorrow.');
            } else {
                alert('Error downloading certificate. Please try again.');
            }
        } finally {
            setVerifying(false);
        }
    };

    if (loading) {
        return (
            <div className="container mt-5">
                <div className="row justify-content-center">
                    <div className="col-md-8 text-center">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Verifying...</span>
                        </div>
                        <h4 className="mt-3">Verifying Certificate...</h4>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container mt-5 mb-5">
            <div className="row justify-content-center">
                <div className="col-md-8">
                    {/* Header */}
                    <div className="text-center mb-4">
                        <h2>Certificate Verification</h2>
                        <p className="text-muted">Dynavative Indo Veritas Safety Services</p>
                    </div>

                    {/* Verification Result Card */}
                    <div className={`card shadow-lg ${valid ? 'border-success' : 'border-danger'}`}>
                        <div className={`card-header text-white ${valid ? 'bg-success' : 'bg-danger'}`}>
                            <h4 className="mb-0">
                                {valid ? (
                                    <>
                                        <i className="fas fa-check-circle me-2"></i>
                                        Certificate Verified
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-times-circle me-2"></i>
                                        Verification Failed
                                    </>
                                )}
                            </h4>
                        </div>

                        <div className="card-body">
                            {valid && certificate ? (
                                <>
                                    {/* Certificate Details */}
                                    <div className="row mb-4">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold text-muted">Work Order Number</label>
                                            <p className="fs-5">{certificate.work_order_number}</p>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold text-muted">Form Number</label>
                                            <p className="fs-5">{certificate.form_number}</p>
                                        </div>
                                    </div>

                                    <div className="row mb-4">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold text-muted">Form Type</label>
                                            <p className="fs-5">{certificate.form_type}</p>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold text-muted">Equipment Type</label>
                                            <p className="fs-5">{certificate.equipment_type}</p>
                                        </div>
                                    </div>

                                    <div className="row mb-4">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold text-muted">Inspector Name</label>
                                            <p className="fs-5">{certificate.inspector_name}</p>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold text-muted">Client Name</label>
                                            <p className="fs-5">{certificate.client_name}</p>
                                        </div>
                                    </div>

                                    <div className="row mb-4">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold text-muted">Inspection Date</label>
                                            <p className="fs-5">
                                                {certificate.inspection_date
                                                    ? new Date(certificate.inspection_date).toLocaleDateString()
                                                    : 'N/A'}
                                            </p>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold text-muted">Certificate Status</label>
                                            <p>
                                                <span className="badge bg-success fs-6">
                                                    {({
                                                        submitted_by_junior: 'Submitted By Inspector',
                                                        sent_back_by_senior: 'Sent Back By Reviewer',
                                                        reviewed_by_senior: 'Reviewed By Reviewer',
                                                        approved_by_admin: 'Approved By Admin'
                                                    }[certificate.status] || certificate.status.replace(/_/g, ' ')).toUpperCase()}
                                                </span>
                                            </p>
                                        </div>
                                    </div>

                                    <div className="row mb-4">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold text-muted">Issued Date</label>
                                            <p className="fs-5">
                                                {certificate.issued_date
                                                    ? new Date(certificate.issued_date).toLocaleDateString()
                                                    : 'N/A'}
                                            </p>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold text-muted">Total Verifications</label>
                                            <p className="fs-5">{certificate.total_verifications}</p>
                                        </div>
                                    </div>

                                    {/* Validity Banner */}
                                    <div className="alert alert-success" role="alert">
                                        <i className="fas fa-shield-alt me-2"></i>
                                        <strong>This certificate is genuine</strong> and was issued by Dynavative Indo Veritas Safety Services.
                                    </div>

                                    {/* Download Section */}
                                    <div className="text-center mt-4">
                                        <button
                                            className="btn btn-primary btn-lg"
                                            onClick={handleDownload}
                                            disabled={!canDownload || verifying}
                                        >
                                            {verifying ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                                    Downloading...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-download me-2"></i>
                                                    Download Certificate
                                                </>
                                            )}
                                        </button>

                                        {!canDownload && (
                                            <p className="text-warning mt-2">
                                                <i className="fas fa-exclamation-triangle me-2"></i>
                                                {downloadMessage}
                                            </p>
                                        )}

                                        <p className="text-muted mt-3 small">
                                            <i className="fas fa-info-circle me-1"></i>
                                            Downloads are limited to 3 per day for security purposes
                                        </p>
                                    </div>
                                </>
                            ) : (
                                <div className="text-center py-5">
                                    <i className="fas fa-exclamation-circle fa-3x text-danger mb-3"></i>
                                    <h5>{error}</h5>
                                    <p className="text-muted">
                                        This verification code is invalid, expired, or has been deactivated.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Footer Info */}
                    <div className="text-center mt-4">
                        <p className="text-muted">
                            <i className="fas fa-lock me-2"></i>
                            This is a secure verification page. All certificate verifications are logged.
                        </p>
                        <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => navigate('/')}
                        >
                            <i className="fas fa-home me-2"></i>
                            Back to Home
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CertificateVerification;
